import React, { useState } from "react";
import {
  Gavel,
  BookOpen,
  FileText,
  AlertOctagon,
  Newspaper,
  ChevronDown,
  ChevronUp,
  Clock,
  User2,
  Calendar,
  ExternalLink,
} from "lucide-react";
import { formatDate, URGENCY } from "@/lib/cmsConstants";

/*
 * Renders any CMS content type as an on-brand card.
 * Used by both the Preview modal (admin) and the public landing pages.
 */
export default function PublicContentCard({ content, preview = false }) {
  const t = content.contentType;
  if (t === "case_law") return <CaseLawCard c={content} preview={preview} />;
  if (t === "act") return <ActCard c={content} preview={preview} />;
  if (t === "circular") return <CircularCard c={content} preview={preview} />;
  if (t === "alert") return <AlertCard c={content} preview={preview} />;
  if (t === "blog") return <BlogCard c={content} preview={preview} />;
  return null;
}

// ---------- Case Law ----------
function CaseLawCard({ c }) {
  const [open, setOpen] = useState(false);
  const f = c.fields || {};
  return (
    <article className="p-5 rounded-lg border border-[#e0e0e8] bg-white flex flex-col gap-3">
      <div className="flex items-center gap-2 flex-wrap text-[12px] text-[#6b6bb5]">
        <span className="inline-flex items-center gap-1.5 font-medium">
          <Gavel className="h-3.5 w-3.5" />
          {f.court || "Court"}
        </span>
        {f.judgmentDate && <span>· {formatDate(f.judgmentDate)}</span>}
        {f.citation && (
          <span className="ml-auto px-2 py-0.5 rounded-md bg-[#f5f5f8] text-[#2d2d7a] font-medium">
            {f.citation}
          </span>
        )}
      </div>
      <h3 className="text-[16px] font-bold text-[#1a1a2e] leading-snug">
        {c.title}
      </h3>
      {f.jurisdiction && (
        <div className="text-[12px] text-[#888]">Jurisdiction: {f.jurisdiction}</div>
      )}
      {f.keyPrinciple && (
        <div
          className="p-3 rounded-md border-l-4"
          style={{
            background: "#f5f5f8",
            borderLeftColor: "#2d2d7a",
          }}
        >
          <div className="text-[11px] uppercase tracking-wider text-[#2d2d7a] font-semibold mb-1">
            Key Principle
          </div>
          <div className="text-[13px] text-[#1a1a2e] leading-snug">
            {f.keyPrinciple}
          </div>
        </div>
      )}
      {f.summary && (
        <RichView html={f.summary} className="text-[13px] text-[#2c2c3e]" />
      )}
      {f.impact && (
        <div className="text-[12.5px] text-[#2c2c3e]">
          <span className="font-semibold">Impact: </span>
          {f.impact}
        </div>
      )}
      {f.fullText && (
        <div>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="text-[12px] font-medium text-[#6b6bb5] hover:underline inline-flex items-center gap-1"
          >
            {open ? "Hide Full Judgment" : "Read Full Judgment"}
            {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
          {open && (
            <div className="mt-3 pt-3 border-t border-[#e0e0e8]">
              <RichView html={f.fullText} />
            </div>
          )}
        </div>
      )}
      <CategoryBadges categories={c.category} />
    </article>
  );
}

// ---------- Act ----------
function ActCard({ c }) {
  const [openIdx, setOpenIdx] = useState(null);
  const f = c.fields || {};
  const sections = f.sections || [];
  return (
    <article className="p-5 rounded-lg border border-[#e0e0e8] bg-white flex flex-col gap-3">
      <div className="flex items-center gap-2 flex-wrap text-[12px] text-[#6b6bb5]">
        <span className="inline-flex items-center gap-1.5 font-medium">
          <BookOpen className="h-3.5 w-3.5" />
          {f.authority || "Authority"}
        </span>
        {f.enactmentDate && <span>· Enacted {formatDate(f.enactmentDate)}</span>}
        {f.amendmentDate && (
          <span>· Amended {formatDate(f.amendmentDate)}</span>
        )}
      </div>
      <div>
        <h3 className="text-[16px] font-bold text-[#1a1a2e] leading-snug">
          {c.title}
        </h3>
        {f.shortName && (
          <div className="text-[12px] text-[#888] mt-0.5">{f.shortName}</div>
        )}
      </div>
      {f.description && <RichView html={f.description} />}
      {sections.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <div className="text-[11px] uppercase tracking-wider text-[#6b6bb5] font-semibold">
            Key Sections
          </div>
          {sections.map((s, i) => (
            <div
              key={i}
              className="rounded-md border border-[#e0e0e8] overflow-hidden"
            >
              <button
                type="button"
                onClick={() => setOpenIdx(openIdx === i ? null : i)}
                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#f8f9fc] transition-colors"
              >
                <span className="text-[12px] font-semibold text-[#2d2d7a] w-16 shrink-0">
                  {s.number}
                </span>
                <span className="flex-1 text-[13px] text-[#1a1a2e] font-medium">
                  {s.title}
                </span>
                {openIdx === i ? (
                  <ChevronUp className="h-3.5 w-3.5 text-[#6b6bb5]" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5 text-[#6b6bb5]" />
                )}
              </button>
              {openIdx === i && (
                <div className="px-3 pb-3 pt-1 text-[13px] text-[#2c2c3e] leading-snug">
                  {s.brief}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {f.related && (
        <div className="text-[12.5px] text-[#2c2c3e]">
          <span className="font-semibold">Related: </span>
          {f.related}
        </div>
      )}
      {f.gazetteUrl && (
        <a
          href={f.gazetteUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[12px] text-[#6b6bb5] hover:underline"
        >
          Official Gazette <ExternalLink className="h-3 w-3" />
        </a>
      )}
      <CategoryBadges categories={c.category} />
    </article>
  );
}

// ---------- Circular ----------
function CircularCard({ c }) {
  const f = c.fields || {};
  return (
    <article className="p-5 rounded-lg border border-[#e0e0e8] bg-white flex flex-col gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className="text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded"
          style={{ background: "#2d2d7a", color: "#fff" }}
        >
          {f.docType || "Document"}
        </span>
        {f.authority && (
          <span className="text-[12px] text-[#6b6bb5] font-medium">
            {f.authority}
          </span>
        )}
        {f.refNumber && (
          <span className="text-[12px] text-[#888] ml-auto">{f.refNumber}</span>
        )}
      </div>
      <div>
        <h3 className="text-[16px] font-bold text-[#1a1a2e] leading-snug">
          {c.title}
        </h3>
        {f.subject && (
          <p className="text-[12.5px] text-[#2c2c3e] mt-0.5">{f.subject}</p>
        )}
      </div>
      <div className="flex items-center gap-3 text-[12px] text-[#888] flex-wrap">
        {f.issueDate && <span>Issued: {formatDate(f.issueDate)}</span>}
        {f.effectDate && <span>Effective: {formatDate(f.effectDate)}</span>}
      </div>
      {f.plainSummary && (
        <div className="p-3 rounded-md bg-[#f5f5f8]">
          <div className="text-[11px] uppercase tracking-wider text-[#6b6bb5] font-semibold mb-1">
            In Plain Language
          </div>
          <RichView html={f.plainSummary} className="text-[13px]" />
        </div>
      )}
      {Array.isArray(f.actions) && f.actions.length > 0 && (
        <div
          className="p-3 rounded-md border-l-4"
          style={{ background: "#fff8e6", borderLeftColor: "#e67e22" }}
        >
          <div className="text-[11px] uppercase tracking-wider text-[#e67e22] font-semibold mb-1">
            Action Required
          </div>
          <ul className="flex flex-col gap-1 text-[13px] text-[#1a1a2e]">
            {f.actions.filter(Boolean).map((a, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-[#e67e22]">•</span>
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {f.fullText && (
        <details className="text-[13px]">
          <summary className="cursor-pointer text-[#6b6bb5] font-medium">
            Full text
          </summary>
          <div className="mt-2">
            <RichView html={f.fullText} />
          </div>
        </details>
      )}
      <CategoryBadges categories={c.category} />
    </article>
  );
}

// ---------- Alert ----------
function AlertCard({ c }) {
  const f = c.fields || {};
  const u = URGENCY.find((x) => x.key === (f.urgencyLevel || "low")) || URGENCY[2];
  return (
    <article
      className="p-4 rounded-lg border flex gap-3"
      style={{
        background: "#fff",
        borderColor: "#e0e0e8",
        borderLeft: `4px solid ${u.color}`,
      }}
    >
      <div
        className="h-9 w-9 rounded-md inline-flex items-center justify-center shrink-0"
        style={{ background: u.bg, color: u.color }}
      >
        <AlertOctagon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="text-[11px] uppercase tracking-wider font-semibold"
            style={{ color: u.color }}
          >
            {u.label}
          </span>
          {f.effectiveDate && (
            <span className="text-[12px] text-[#888]">
              · {formatDate(f.effectiveDate)}
            </span>
          )}
          {f.authority && (
            <span className="text-[12px] text-[#6b6bb5]">· {f.authority}</span>
          )}
        </div>
        <h4 className="text-[15px] font-bold text-[#1a1a2e] leading-snug">
          {f.headline || c.title}
        </h4>
        {f.body && <RichView html={f.body} className="text-[13px]" />}
        {Array.isArray(f.actions) && f.actions.length > 0 && (
          <ul className="flex flex-col gap-1 text-[13px] text-[#1a1a2e] mt-1">
            {f.actions.filter(Boolean).map((a, i) => (
              <li key={i} className="flex gap-2">
                <span style={{ color: u.color }}>•</span>
                <span>{a}</span>
              </li>
            ))}
          </ul>
        )}
        {Array.isArray(f.links) && f.links.filter((l) => l.url).length > 0 && (
          <div className="flex flex-wrap gap-3 mt-1">
            {f.links
              .filter((l) => l.url)
              .map((l, i) => (
                <a
                  key={i}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[12px] text-[#6b6bb5] hover:underline inline-flex items-center gap-1"
                >
                  {l.label || l.url}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ))}
          </div>
        )}
        <CategoryBadges categories={c.category} />
      </div>
    </article>
  );
}

// ---------- Blog ----------
function BlogCard({ c }) {
  const f = c.fields || {};
  return (
    <article className="p-5 rounded-lg border border-[#e0e0e8] bg-white flex flex-col gap-3">
      {f.coverImage && (
        <img
          src={f.coverImage}
          alt=""
          className="w-full h-48 object-cover rounded-md"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      )}
      <div className="flex items-center gap-3 text-[12px] text-[#888]">
        {f.author && (
          <span className="inline-flex items-center gap-1">
            <User2 className="h-3 w-3" /> {f.author}
          </span>
        )}
        {f.publishDate && (
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3 w-3" /> {formatDate(f.publishDate)}
          </span>
        )}
        {f.readingTime && (
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> {f.readingTime} min read
          </span>
        )}
      </div>
      <h2 className="text-[18px] font-bold text-[#1a1a2e] leading-snug">
        {c.title}
      </h2>
      {f.designation && (
        <div className="text-[12px] text-[#6b6bb5]">— {f.designation}</div>
      )}
      {f.body && <RichView html={f.body} />}
      {f.tags && (
        <div className="flex flex-wrap gap-1.5">
          {f.tags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
            .map((t) => (
              <span
                key={t}
                className="text-[11px] px-2 py-0.5 rounded-md bg-[#f5f5f8] text-[#2d2d7a]"
              >
                #{t}
              </span>
            ))}
        </div>
      )}
      <CategoryBadges categories={c.category} />
    </article>
  );
}

// ---------- Shared ----------
function CategoryBadges({ categories }) {
  if (!categories?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 pt-2 border-t border-[#e0e0e8]">
      {categories.map((c) => (
        <span
          key={c}
          className="text-[11px] px-2 py-0.5 rounded-full"
          style={{ background: "#f5f5f8", color: "#2d2d7a" }}
        >
          {c}
        </span>
      ))}
    </div>
  );
}

/* Renders sanitized HTML. Content is trusted (admin-authored + sanitized on
   backend), but we still render inside a scoped wrapper for stable typography. */
export function RichView({ html, className }) {
  return (
    <div
      className={`cms-rich text-[13.5px] leading-relaxed text-[#2c2c3e] ${className || ""}`}
      dangerouslySetInnerHTML={{ __html: html || "" }}
    />
  );
}
